import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface SelectFilterConfig {
  label: string;
  value: string | string[];
  width?: number;
  options: { label: string; value: string }[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  /**
   * Renders the filter label inline inside the control with a selection pill,
   * instead of stacked above. Use for toolbar-style filter bars where controls
   * sit inline rather than in a labeled column layout.
   */
  pillStyle?: boolean;
  /**
   * Icon rendered at the leading edge of a pill-style control.
   * Ignored when pillStyle is false/undefined.
   */
  startAdornment?: React.ReactNode;
}

export interface FiltersBarProps {
  searchPlaceholder?: string;
  searchValue?: string;
  searchWidth?: number;
  onSearchChange: (value: string) => void;
  filters?: SelectFilterConfig[];
  /** Hide the stacked "Search" label above the input (default false). */
  hideSearchLabel?: boolean;
  /** Optional leading icon rendered inside the search input (e.g. a magnifying glass). */
  searchStartAdornment?: React.ReactNode;
}

const PILL_SELECT_SX = {
  height: 38,
  backgroundColor: 'background.paper',
  borderRadius: '8px',
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.400' },
} as const;

const DEFAULT_SELECT_SX = {
  height: 36,
  backgroundColor: 'background.paper',
  borderRadius: 1,
  fontSize: 13,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
} as const;

function PillRenderValue({
  selected,
  label,
  options,
}: {
  selected: string[];
  label: string;
  options: { label: string; value: string }[];
}) {
  const firstLabel =
    selected.length > 0
      ? options.find((o) => selected.includes(o.value))?.label ?? selected[0]
      : '';
  const extra = selected.length - 1;
  return (
    <Stack component="span" direction="row" sx={{ alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography component="span" sx={{ fontSize: 13, color: 'text.primary', flexShrink: 0 }}>
        {label}
      </Typography>
      {selected.length > 0 && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: 150,
            px: 0.75,
            py: '1px',
            borderRadius: '9999px',
            backgroundColor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            fontSize: 12,
            fontWeight: 500,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {firstLabel}
          {extra > 0 ? ` +${extra}` : ''}
        </Box>
      )}
    </Stack>
  );
}

const FiltersBar = React.memo(function FiltersBar({
  searchPlaceholder = 'Search...',
  searchValue = '',
  searchWidth = 384,
  onSearchChange,
  filters = [],
  hideSearchLabel = false,
  searchStartAdornment,
}: FiltersBarProps) {
  const [inputValue, setInputValue] = useState(searchValue ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = useRef(searchValue ?? '');
  // Ties the visible "Search" caption to the input so the field has an accessible
  // name. With the caption hidden there is nothing to point at, so the placeholder
  // (which always leads with "Search…") becomes the name instead.
  const searchInputId = useId();

  // Sync local state only for true external changes (e.g., reset), not debounced echoes
  useEffect(() => {
    const normalized = searchValue ?? '';
    if (normalized !== lastEmittedRef.current) {
      setInputValue(normalized);
      lastEmittedRef.current = normalized;
    }
  }, [searchValue]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = val;
      onSearchChange(val);
    }, 300);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {!hideSearchLabel && (
          <Typography
            component="label"
            htmlFor={searchInputId}
            sx={{ fontSize: 13, fontWeight: 500, color: '#616161' }}
          >
            Search
          </Typography>
        )}
        <TextField
          id={searchInputId}
          value={inputValue}
          onChange={handleSearchInput}
          placeholder={searchPlaceholder}
          size="small"
          inputProps={hideSearchLabel ? { 'aria-label': searchPlaceholder } : undefined}
          InputProps={
            searchStartAdornment
              ? {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      {searchStartAdornment}
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          sx={{
            width: searchWidth,
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              fontSize: 13,
              '& fieldset': { borderColor: 'divider' },
              '&:hover fieldset': { borderColor: '#bdbdbd' },
            },
            '& input::placeholder': { color: '#9e9e9e', opacity: 1 },
          }}
        />
      </Box>

      {filters.map((filter) =>
        filter.pillStyle ? (
          // Pill style: label lives inside the control; no stacked label above.
          <FormControl
            key={filter.label}
            size="small"
            sx={{ minWidth: filter.width ?? 160, maxWidth: 280 }}
          >
            <Select
              multiple={!!filter.multiple}
              displayEmpty
              // The facet name is painted inside the control, so there is no <label>
              // to associate — name the combobox explicitly.
              inputProps={{ 'aria-label': filter.label }}
              value={filter.multiple ? (Array.isArray(filter.value) ? filter.value : []) : (filter.value as string)}
              onChange={(e) => filter.onChange(e.target.value as string | string[])}
              startAdornment={
                filter.startAdornment ? (
                  <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.25 }}>
                    {filter.startAdornment}
                  </InputAdornment>
                ) : undefined
              }
              renderValue={
                filter.multiple
                  ? (selected) => (
                      <PillRenderValue
                        selected={selected as string[]}
                        label={filter.label}
                        options={filter.options}
                      />
                    )
                  : undefined
              }
              sx={PILL_SELECT_SX}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {filter.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, py: 0.5 }}>
                  {filter.multiple && (
                    <Checkbox
                      checked={Array.isArray(filter.value) && filter.value.includes(opt.value)}
                      size="small"
                      sx={{ py: 0 }}
                    />
                  )}
                  <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          // Default style: label stacked above the control.
          <Box key={filter.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#616161' }}>
              {filter.label}
            </Typography>
            <FormControl size="small" sx={{ width: filter.width ?? 160 }}>
              {filter.multiple ? (
                <Select
                  multiple
                  inputProps={{ 'aria-label': filter.label }}
                  value={Array.isArray(filter.value) ? filter.value : []}
                  onChange={(e) => filter.onChange(e.target.value as string[])}
                  displayEmpty
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) {
                      return <span style={{ color: '#9e9e9e' }}>All {filter.label}</span>;
                    }
                    if ((selected as string[]).length > 2) {
                      return `${(selected as string[]).length} selected`;
                    }
                    return (selected as string[])
                      .map((val) => filter.options.find((o) => o.value === val)?.label ?? val)
                      .join(', ');
                  }}
                  sx={DEFAULT_SELECT_SX}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {filter.options
                    .filter((opt) => opt.value !== '')
                    .map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, py: 0.5 }}>
                        <Checkbox
                          checked={Array.isArray(filter.value) && filter.value.includes(opt.value)}
                          size="small"
                          sx={{ py: 0 }}
                        />
                        <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13 }} />
                      </MenuItem>
                    ))}
                </Select>
              ) : (
                <Select
                  inputProps={{ 'aria-label': filter.label }}
                  value={filter.value as string}
                  onChange={(e) => filter.onChange(e.target.value as string)}
                  displayEmpty
                  sx={DEFAULT_SELECT_SX}
                >
                  {filter.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Box>
        ),
      )}
    </Stack>
  );
});

export default FiltersBar;
